import { create } from "zustand";
import { AssetLoader } from "./AssetLoader";
import { AudioManager } from "./AudioManager";
import { tokens } from "@/art/tokens";
import { sleep } from "@/utils/clamp";
import type { SceneDefinition, SceneId } from "@/scenes/SceneContract";

/**
 * 씬 스택 + 전환 상태 관리. 웹 라우터 대체물.
 *
 * phase 동안:
 *   idle       → 현재 top 씬 정상 렌더
 *   preloading → 다음 씬 애셋 로드 + BGM 크로스페이드 시작. top 씬은 유지
 *   exiting    → top 씬 exit 애니메이션
 *   entering   → 다음 씬 enter 애니메이션
 *
 * SceneHost 는 phase·stack·pending 을 읽어 무엇을 어떻게 렌더할지 결정.
 */

interface StackFrame {
    id: SceneId;
    params: unknown;
    at: number;
}

export interface LoadableSceneDefinition {
    load: () => Promise<SceneDefinition>;
    loaded?: SceneDefinition;
}

export type SceneRegistryEntry = SceneDefinition | LoadableSceneDefinition;

export type ScenePhase = "idle" | "preloading" | "exiting" | "entering";

interface SceneState {
    registry: Record<SceneId, SceneRegistryEntry>;
    stack: StackFrame[];
    pending: StackFrame | null;
    phase: ScenePhase;
}

interface SceneActions {
    registerScenes: (entries: Array<[SceneId, SceneRegistryEntry]>) => void;
    push: (id: SceneId, params?: unknown) => Promise<void>;
    replace: (id: SceneId, params?: unknown) => Promise<void>;
    pop: () => Promise<void>;
    reset: (id: SceneId, params?: unknown) => Promise<void>;
}

export const useSceneStore = create<SceneState & SceneActions>((set, get) => ({
    registry: {} as Record<SceneId, SceneRegistryEntry>,
    stack: [],
    pending: null,
    phase: "idle",

    registerScenes: (entries) => {
        const next = { ...get().registry };
        for (const [id, def] of entries) {
            next[id] = def;
        }
        set({ registry: next });
    },

    push: async (id, params) => {
        await runTransition(set, get, id, params, { pop: false });
    },

    replace: async (id, params) => {
        await runTransition(set, get, id, params, { pop: true });
    },

    pop: async () => {
        const { stack } = get();
        if (stack.length <= 1) return;
        const newTop = stack[stack.length - 2];
        await runTransition(set, get, newTop.id, newTop.params, { pop: true, popExisting: true });
    },

    reset: async (id, params) => {
        set({ stack: [], pending: null, phase: "idle" });
        await runTransition(set, get, id, params, { pop: false });
    },
}));

async function runTransition(
    set: (partial: Partial<SceneState>) => void,
    get: () => SceneState & SceneActions,
    id: SceneId,
    params: unknown,
    opts: { pop: boolean; popExisting?: boolean },
): Promise<void> {
    const def = await resolveSceneDefinition(set, get, id);
    if (!def) {
        console.error(`[SceneManager] scene not registered: ${id}`);
        return;
    }

    // 2026-04-23: 전환 재진입 가드 — 강화 버전.
    //
    // 씬 전환이 진행 중일 때 유저가 빠르게 버튼을 연타하면 여러 runTransition 이
    // 병렬로 돌며 currentStack 을 각자 다르게 읽어 stack 이 꼬이는 문제가 있었다.
    //
    // 이전 버전은 preloading/exiting 만 드롭하고 "entering" 은 허용했지만, entering
    // 단계에서도 두 번째 transition 이 들어오면 enterDuration sleep 와 병렬로 새
    // preloading 이 돌고, 결국 stack 에 중복 프레임이 쌓이거나 BackButton 연타 시
    // pop 이 두 번 실행되어 splash 까지 튕기는 버그가 재현됐다.
    //
    // 2026-04-23 수정: 모든 non-idle phase 에서 드롭. entering 애니메이션이 끝날 때까지
    // 유저 입력은 무시 (enterDuration 이 짧아서 UX 저하 없음). reset() 은 자체적으로
    // phase="idle" 로 먼저 세팅하므로 이 가드를 통과 — 부팅/긴급 초기화는 영향 없음.
    const currentPhase = get().phase;
    if (currentPhase !== "idle") {
        return;
    }

    const frame: StackFrame = { id, params: params ?? {}, at: Date.now() };

    // 1) preload — 리소스 실패는 관대하게. 씬 전환 자체를 막으면 게임이 죽음.
    set({ pending: frame, phase: "preloading" });
    try {
        await AssetLoader.preload(def.manifest.assets);
    } catch (err) {
        console.warn("[SceneManager] preload failed, continuing:", err);
    }
    if (def.manifest.bgm !== undefined) {
        // undefined 면 유지, "" 면 정지, 경로면 재생.
        try {
            await AudioManager.crossfadeTo(def.manifest.bgm || undefined);
        } catch (err) {
            console.warn("[SceneManager] bgm crossfade failed, continuing:", err);
        }
    }

    // 2) exit previous (if any)
    const currentStack = get().stack;
    if (currentStack.length > 0) {
        const exitDuration = def.manifest.transitions.exitDuration ?? tokens.timing.sceneExit;
        set({ phase: "exiting" });
        await sleep(exitDuration);
    }

    // 3) push/replace/pop stack
    let nextStack: StackFrame[];
    if (opts.popExisting) {
        // pop 경로: top 을 버리고 target 으로 돌아감 (stack 은 target 이 이미 있음)
        nextStack = currentStack.slice(0, -1);
    } else if (opts.pop) {
        // replace: top 을 버리고 target 으로 대체
        nextStack = [...currentStack.slice(0, -1), frame];
    } else {
        // push
        nextStack = [...currentStack, frame];
    }
    set({ stack: nextStack, pending: null, phase: "entering" });

    // 4) enter animation duration — SceneHost 가 AnimatePresence 로 처리, 여기선 지연만
    const enterDuration = def.manifest.transitions.enterDuration ?? tokens.timing.sceneEnter;
    await sleep(enterDuration);
    set({ phase: "idle" });
}

function isLoadableSceneDefinition(entry: SceneRegistryEntry): entry is LoadableSceneDefinition {
    return typeof (entry as LoadableSceneDefinition).load === "function";
}

export function getLoadedSceneDefinition(entry?: SceneRegistryEntry | null): SceneDefinition | null {
    if (!entry) return null;
    if (isLoadableSceneDefinition(entry)) {
        return entry.loaded ?? null;
    }
    return entry;
}

async function resolveSceneDefinition(
    set: (partial: Partial<SceneState>) => void,
    get: () => SceneState & SceneActions,
    id: SceneId,
): Promise<SceneDefinition | null> {
    const entry = get().registry[id];
    if (!entry) return null;
    if (!isLoadableSceneDefinition(entry)) return entry;
    if (entry.loaded) return entry.loaded;
    const loaded = await entry.load();
    const nextRegistry = { ...get().registry, [id]: { ...entry, loaded } };
    set({ registry: nextRegistry });
    return loaded;
}

/** 편의 훅 — 현재 씬. */
export function useCurrentScene() {
    return useSceneStore(s => {
        const top = s.stack[s.stack.length - 1];
        if (!top) return null;
        const def = getLoadedSceneDefinition(s.registry[top.id]);
        return def ? { id: top.id, params: top.params, definition: def } : null;
    });
}
